/**
 * JOB-4193. Which time zone the office app shows times in.
 *
 * Order: the zone the API sends (the company's "Company time zone" setting, or
 * the server's TZ), else the viewer's own browser zone. Never a hard-coded
 * zone: Los Angeles was right for one company and three hours off for Ohio.
 */

export interface OfficeTimeZoneOption {
  id: string;
  label: string;
}

/** The zones the Settings dropdown offers: every zone the API can derive from an HQ. */
export const OFFICE_TIME_ZONES: OfficeTimeZoneOption[] = [
  {id: 'America/New_York', label: 'Eastern (New York)'},
  {id: 'America/Chicago', label: 'Central (Chicago)'},
  {id: 'America/Denver', label: 'Mountain (Denver)'},
  {id: 'America/Phoenix', label: 'Mountain, no daylight saving (Phoenix)'},
  {id: 'America/Los_Angeles', label: 'Pacific (Los Angeles)'},
  {id: 'America/Anchorage', label: 'Alaska (Anchorage)'},
  {id: 'America/Adak', label: 'Hawaii-Aleutian (Adak)'},
  {id: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)'},
  {id: 'America/Puerto_Rico', label: 'Atlantic (Puerto Rico)'},
  {id: 'America/Halifax', label: 'Atlantic (Halifax)'},
  {id: 'America/Moncton', label: 'Atlantic (Moncton)'},
  {id: 'America/St_Johns', label: 'Newfoundland (St. John\'s)'},
  {id: 'America/Toronto', label: 'Eastern (Toronto)'},
  {id: 'America/Iqaluit', label: 'Eastern (Iqaluit)'},
  {id: 'America/Winnipeg', label: 'Central (Winnipeg)'},
  {id: 'America/Regina', label: 'Central, no daylight saving (Regina)'},
  {id: 'America/Edmonton', label: 'Mountain (Edmonton)'},
  {id: 'America/Whitehorse', label: 'Yukon (Whitehorse)'},
  {id: 'America/Vancouver', label: 'Pacific (Vancouver)'},
];

const warnedValues = new Set<string>();

/** True when the browser knows the zone; a bad zone makes Intl throw. */
export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== 'string' || timeZone.trim() === '') {
    return false;
  }
  try {
    new Intl.DateTimeFormat('en-US', {timeZone});
    return true;
  } catch (e) {
    return false;
  }
}

/** The viewer's own zone, or null if the browser cannot say. */
export function browserTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(zone) ? zone : null;
  } catch (e) {
    return null;
  }
}

/**
 * The zone to format with: the API's zone when it is a real one, else the
 * browser's. Null only when neither is known; the caller then formats in the
 * browser's local time, which is the same thing without a name.
 */
export function resolveDisplayTimeZone(fromApi: unknown): string | null {
  if (isValidTimeZone(fromApi)) {
    return fromApi;
  }
  if (fromApi !== null && fromApi !== undefined && fromApi !== '') {
    warnOnce(String(fromApi));
  }

  return browserTimeZone();
}

/** Logged once per value: the pipe runs on every change detection. */
function warnOnce(value: string): void {
  if (warnedValues.has(value)) {
    return;
  }
  warnedValues.add(value);
  console.warn(`Office time zone "${value}" is not a known time zone; showing times in this browser's zone.`);
}
